import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const kanjiMap: Record<string, string> = {
  pokemon: "ポケ",
  magic: "魔術",
  yugioh: "遊戯",
  digimon: "デジ",
  onepiece: "海賊",
  flesh: "血肉",
  dragonball: "龍珠",
  starwars: "銀河",
  lorcana: "物語",
  finalfantasy: "幻想"
};
const metaMap: Record<string, string> = {
  pokemon: "For: collectors, competitive · Era: Base to SV",
  magic: "For: commander, modern · Format: All sets",
  yugioh: "For: dueling, meta · Rarity: Secret to Common",
  digimon: "For: digital battles · Style: Strategic",
  onepiece: "For: grand line adventures · Type: TCG",
  flesh: "For: hero combat · Depth: High complexity",
  dragonball: "For: super warriors · Power: Over 9000",
  starwars: "For: galactic conquest · Side: Light/Dark",
  lorcana: "For: ink casting · Theme: Disney magic",
  finalfantasy: "For: crystal quests · Strategy: Tactical"
};
function getKanji(slug: string, name: string): string {
  const key = slug?.toLowerCase() ?? "";
  if (kanjiMap[key]) return kanjiMap[key];
  const nameKey = name?.toLowerCase() ?? "";
  for (const k of Object.keys(kanjiMap)) {
    if (nameKey.includes(k)) return kanjiMap[k];
  }
  return "";
}
function getMeta(slug: string, name: string): string {
  const key = slug?.toLowerCase() ?? "";
  if (metaMap[key]) return metaMap[key];
  const nameKey = name?.toLowerCase() ?? "";
  for (const k of Object.keys(metaMap)) {
    if (nameKey.includes(k)) return metaMap[k];
  }
  return "Premium TCG inventory · Authenticated";
}
function CollectionsGridConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-border/40">
        <div className="lg:col-span-5 lg:row-span-2 h-[400px] lg:h-[820px] bg-card animate-pulse" />
        {[...Array(4)].map((_, i) => <div key={i} data-index={i} className="lg:col-span-7 lg:col-start-6 h-[400px] bg-card animate-pulse" style={{
        gridColumn: i % 2 === 0 ? "6 / span 4" : "9 / span 4"
      }} />)}
      </div>;
  }
  if (error) {
    return <div className="border border-border p-12 text-center">
        <p className="text-sm text-muted-foreground font-mono uppercase tracking-widest mb-4">
          Unable to load inventory
        </p>
        <Button onClick={refetch} variant="outline" className="rounded-md">
          Retry
        </Button>
      </div>;
  }
  if (terms.length === 0) {
    return <div className="border border-border p-12 text-center">
        <p className="text-sm text-muted-foreground font-mono uppercase tracking-widest">
          No categories available
        </p>
      </div>;
  }
  const feature = terms[0];
  const grid = terms.slice(1, 5);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
      {}
      <Link to={feature.link || "#"} data-index={0} className="group lg:col-span-5 lg:row-span-2 relative block border border-border bg-card overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 rounded-lg shadow-md">
        <div className="relative h-full flex flex-col min-h-[520px] lg:min-h-[820px]">
          <div className="flex-1 flex items-center justify-center overflow-hidden p-12">
            {feature.image_url ? <img src={feature.image_url} alt={feature.name} loading="lazy" decoding="async" className="max-h-[420px] lg:max-h-[560px] w-auto object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]" /> : <div className="text-muted-foreground/30 font-serif italic text-9xl">
                {getKanji(feature.slug, feature.name)}
              </div>}
          </div>

          <div className="relative px-8 pb-10 pt-6">
            <span aria-hidden="true" className="absolute left-8 bottom-20 text-[7rem] lg:text-[9rem] leading-none font-serif text-foreground/[0.08] select-none pointer-events-none">
              {getKanji(feature.slug, feature.name)}
            </span>
            <div className="relative z-10">
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary mb-3">
                Featured Vault
              </p>
              <h3 className="font-default italic text-5xl lg:text-6xl font-light text-foreground mb-3">
                {feature.name}
              </h3>
              <p className="text-xs font-mono text-muted-foreground tracking-wide">
                {getMeta(feature.slug, feature.name)}
              </p>
              <div className="mt-6 h-px w-0 bg-primary transition-all duration-700 ease-out group-hover:w-full" />
            </div>
          </div>
        </div>
      </Link>

      {}
      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
        {grid.map((term, i) => <Link key={term.id} to={term.link || "#"} data-index={i + 1} className="group relative block border border-border bg-card overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1 rounded-lg shadow-md">
            <div className="relative h-full flex flex-col min-h-[380px] lg:min-h-[398px]">
              <div className="flex-1 flex items-center justify-center overflow-hidden p-8">
                {term.image_url ? <img src={term.image_url} alt={term.name} loading="lazy" decoding="async" className="max-h-[200px] lg:max-h-[240px] w-auto object-contain transition-transform duration-700 ease-out group-hover:scale-[1.04]" /> : <div className="text-muted-foreground/30 font-serif italic text-7xl">
                    {getKanji(term.slug, term.name)}
                  </div>}
              </div>

              <div className="relative px-6 pb-7 pt-4">
                <span aria-hidden="true" className="absolute left-6 bottom-14 text-[5rem] lg:text-[6rem] leading-none font-serif text-foreground/[0.08] select-none pointer-events-none">
                  {getKanji(term.slug, term.name)}
                </span>
                <div className="relative z-10">
                  <h3 className="font-default italic text-3xl lg:text-4xl font-light text-foreground mb-2">
                    {term.name}
                  </h3>
                  <p className="text-[0.65rem] font-mono text-muted-foreground tracking-wide leading-relaxed">
                    {getMeta(term.slug, term.name)}
                  </p>
                  <div className="mt-4 h-px w-0 bg-primary transition-all duration-700 ease-out group-hover:w-full" />
                </div>
              </div>
            </div>
          </Link>)}
      </div>
    </div>;
}
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 5,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
export default function BrowseByGame() {
  return <section data-section-id="4076" id="browse-by-game" className="bg-background text-foreground py-24 lg:py-32 px-6 lg:px-12 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mb-16 lg:mb-24">
          <div className="lg:col-span-7">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-8">
              02 &mdash; Inventory
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl xl:text-[5rem] leading-[0.95] font-light text-foreground">
              Cards, sorted by{" "}
              <span className="italic">universe.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <p className="text-sm lg:text-base font-default text-muted-foreground leading-relaxed">
              Every deck in our vault is curated for the competitive edge. Explore our
              collections by game &mdash; from legendary Pokémon sets to high-stakes
              Magic: The Gathering formats.
            </p>
          </div>
        </div>

        {}
        <EcommerceTermsProvider className="artisan-collections-grid" wp_term_query={wp_term_query}>
          <CollectionsGridConsumer />
        </EcommerceTermsProvider>

        {}
        <div className="mt-16 lg:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t border-border">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">
            Verified Authentic &middot; Secure Vault
          </p>
          <Link to="/ShopPage" className="group inline-flex items-center gap-3 text-sm font-mono uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors duration-300">
            View the full catalog
            <span aria-hidden="true" className="inline-block w-8 h-px bg-current transition-all duration-500 group-hover:w-12" />
          </Link>
        </div>
      </div>
    </section>;
}